/**
 * Tableaux d'affichage (2026-11-03) : un panneau d'affichage posé sur une carte
 * (« Lire le tableau ») montre la liste des duos du dernier tirage, que les
 * admins remplissent après chaque tirage au sort (étage Duos de Copy Camp).
 * Contenu dans la table `notice_boards`, un par objet posé.
 * Sous-chemin `@campus/core/notice-boards` côté serveur, jamais le barrel.
 */

/** Objets du catalogue qui s'ouvrent comme tableau d'affichage. */
export const NOTICE_BOARD_FURNITURE: ReadonlySet<string> = new Set([
  'pl_tableau_duos', 'ch_at-panneau-affichage', 'lz_library_33',
]);

export function isNoticeBoard(furnitureId: string): boolean {
  return NOTICE_BOARD_FURNITURE.has(furnitureId);
}

export const NOTICE_TITLE_MAX = 80;
export const NOTICE_NOTE_MAX = 500;
export const NOTICE_MAX_DUOS = 200;
export const NOTICE_LABEL_MAX = 40;

export interface NoticeDuo {
  /** Deux personnes (ids des membres), trois au plus pour un nombre impair. */
  userIds: string[];
  /** Nom libre du duo (« Duo 1 », un salon…), facultatif. */
  label?: string;
}

export interface NoticeBoardContent {
  title: string;
  note: string;
  duos: NoticeDuo[];
}

export const DEFAULT_NOTICE_TITLE = 'Duos de la semaine';

/** Contenu valide et nettoyé, ou un code d'erreur. */
export function sanitizeNoticeBoard(raw: unknown): NoticeBoardContent | 'INVALID_CONTENT' | 'TOO_MANY_DUOS' | 'INVALID_DUO' {
  if (!raw || typeof raw !== 'object') return 'INVALID_CONTENT';
  const r = raw as { title?: unknown; note?: unknown; duos?: unknown };
  const title = typeof r.title === 'string' ? r.title.trim().slice(0, NOTICE_TITLE_MAX) : '';
  const note = typeof r.note === 'string' ? r.note.replace(/\r\n?/g, '\n').trim().slice(0, NOTICE_NOTE_MAX) : '';
  if (!Array.isArray(r.duos)) return 'INVALID_CONTENT';
  if (r.duos.length > NOTICE_MAX_DUOS) return 'TOO_MANY_DUOS';
  const seen = new Set<string>();
  const duos: NoticeDuo[] = [];
  for (const d of r.duos as unknown[]) {
    if (!d || typeof d !== 'object') return 'INVALID_DUO';
    const { userIds, label } = d as { userIds?: unknown; label?: unknown };
    if (!Array.isArray(userIds) || userIds.length < 2 || userIds.length > 3) return 'INVALID_DUO';
    const ids = userIds.filter((u): u is string => typeof u === 'string' && u.length > 0 && u.length <= 64);
    if (ids.length !== userIds.length || new Set(ids).size !== ids.length) return 'INVALID_DUO';
    // Une personne n'est que dans un duo.
    if (ids.some((u) => seen.has(u))) return 'INVALID_DUO';
    ids.forEach((u) => seen.add(u));
    const name = typeof label === 'string' ? label.trim().slice(0, NOTICE_LABEL_MAX) : '';
    duos.push(name ? { userIds: ids, label: name } : { userIds: ids });
  }
  return { title: title || DEFAULT_NOTICE_TITLE, note, duos };
}
